import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SubnodeOutlined,
} from '@ant-design/icons';
import { App, Button, Card, Empty, Space, Spin, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import {
  DepartmentFormDrawer,
  type DepartmentFormMode,
} from '@/pages/admin/departments/DepartmentFormDrawer';
import type { DepartmentTreeNode } from '@/types/department';
import {
  findDepartmentNode,
  flattenDepartmentUsers,
  toAntdTreeData,
} from '@/utils/department-tree';

export function DepartmentManagementPage() {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<DepartmentTreeNode[]>([]);
  const [antdTree, setAntdTree] = useState<DataNode[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DepartmentFormMode>('create');
  const [parentIdForCreate, setParentIdForCreate] = useState<string | undefined>();

  const selectedDepartment = useMemo(
    () => (selectedId ? findDepartmentNode(treeData, selectedId) : null),
    [selectedId, treeData],
  );

  const managerOptions = useMemo(() => {
    const byId = new Map(
      flattenDepartmentUsers(treeData).map((user) => [
        user.id,
        { value: user.id, label: `${user.name}（${user.phone}）` },
      ]),
    );

    if (selectedDepartment?.managerId && !byId.has(selectedDepartment.managerId)) {
      byId.set(selectedDepartment.managerId, {
        value: selectedDepartment.managerId,
        label: `负责人（ID ${selectedDepartment.managerId}）`,
      });
    }

    return Array.from(byId.values());
  }, [selectedDepartment?.managerId, treeData]);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const data = await departmentsApi.fetchDepartmentTree();
      setTreeData(data);
      setAntdTree(toAntdTreeData(data));
      setExpandedKeys((prev) => {
        if (prev.length > 0) {
          return prev;
        }
        return data.map((item) => item.id);
      });
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载组织架构失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const openDrawer = (mode: DepartmentFormMode, parentId?: string) => {
    setDrawerMode(mode);
    setParentIdForCreate(parentId);
    setDrawerOpen(true);
  };

  const handleDelete = () => {
    if (!selectedDepartment) {
      message.warning('请先选择要删除的部门');
      return;
    }

    if (selectedDepartment.children.length > 0) {
      message.warning('请先删除或移走子部门');
      return;
    }

    modal.confirm({
      title: '确认删除部门',
      content: `确定删除「${selectedDepartment.name}」吗？此操作为软删除。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await departmentsApi.deleteDepartment(selectedDepartment.id);
          message.success('部门已删除');
          setSelectedId(null);
          await loadTree();
        } catch (error) {
          const text = error instanceof ApiError ? error.message : '删除失败';
          message.error(text);
          throw error;
        }
      },
    });
  };

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        组织架构管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        维护部门树结构，支持新增、编辑与删除。部门负责人从当前组织树中的用户选择。
      </Typography.Paragraph>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer('create')}>
            新增根部门
          </Button>
          <Button
            icon={<SubnodeOutlined />}
            disabled={!selectedDepartment}
            onClick={() => openDrawer('createChild', selectedDepartment?.id)}
          >
            新增子部门
          </Button>
          <Button
            icon={<EditOutlined />}
            disabled={!selectedDepartment}
            onClick={() => openDrawer('edit')}
          >
            编辑
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={!selectedDepartment}
            onClick={handleDelete}
          >
            删除
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void loadTree()}>
            刷新
          </Button>
        </Space>

        {selectedDepartment ? (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            已选：{selectedDepartment.name}（{selectedDepartment.code}）
            {selectedDepartment.users.length > 0
              ? ` · ${selectedDepartment.users.length} 名成员`
              : ''}
          </Typography.Text>
        ) : null}

        <Spin spinning={loading}>
          {antdTree.length > 0 ? (
            <Tree
              showLine
              blockNode
              selectedKeys={selectedId ? [selectedId] : []}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys as string[])}
              treeData={antdTree}
              onSelect={(keys) => {
                const key = keys[0];
                setSelectedId(key ? String(key) : null);
              }}
            />
          ) : (
            <Empty description="暂无部门数据" />
          )}
        </Spin>
      </Card>

      <DepartmentFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        tree={treeData}
        managerOptions={managerOptions}
        editingDepartment={drawerMode === 'edit' ? selectedDepartment : null}
        parentIdForCreate={parentIdForCreate}
        onClose={() => setDrawerOpen(false)}
        onSuccess={() => void loadTree()}
      />
    </div>
  );
}
